/**
 * Adresses de l'API du back-office (Lot 7 étapes 07-08).
 *
 * Toutes les routes admin sont servies sous `/api/v1/admin/<domaine>/…` :
 * - par la fonction `api_admin` une fois déployée ;
 * - ET par la fonction `api` pendant la bascule (mêmes chemins).
 *
 * `REACT_APP_ADMIN_API_URL` (au build) choisit la fonction ; par défaut, la
 * fonction `api` actuelle — la console marche que `api_admin` soit déployée
 * ou non. Les routes utilisateur (profil `/admin/users/me`, CRUD de lecture
 * et d'édition…) restent sur `REACT_APP_API_URL`.
 *
 * Un domaine = UNE constante de chemin (ADMIN_PATHS) ; jamais de chemin admin
 * écrit en dur dans un service.
 */

/** Fonction `api` (utilisateur + mobile + chemins admin de transition). */
export const API_URL =
  process.env.REACT_APP_API_URL || 'https://api-nhhrvlctxa-uc.a.run.app';

/** Fonction qui sert le back-office (`api_admin` après bascule). */
export const ADMIN_API_URL = process.env.REACT_APP_ADMIN_API_URL || API_URL;

const ADMIN_PREFIX = '/api/v1/admin';

/** Chemin canonique de chaque domaine admin. */
export const ADMIN_PATHS = {
  credits: `${ADMIN_PREFIX}/credits`,
  creditsSuivi: `${ADMIN_PREFIX}/credits/suivi`,
  dashboard: `${ADMIN_PREFIX}/dashboard`,
  creditPackages: `${ADMIN_PREFIX}/credit-packages`,
  creditConfig: `${ADMIN_PREFIX}/credit-config`,
  pispi: `${ADMIN_PREFIX}/pispi`,
  parrainage: `${ADMIN_PREFIX}/parrainage`,
  revenus: `${ADMIN_PREFIX}/revenus`,
  marketplace: `${ADMIN_PREFIX}/marketplace`,
  servicesCatalog: `${ADMIN_PREFIX}/services-catalog`,
  enseignantVerification: `${ADMIN_PREFIX}/enseignant-verification`,
  moderation: `${ADMIN_PREFIX}/moderation`,
  pricing: `${ADMIN_PREFIX}/pricing`,
  publicite: `${ADMIN_PREFIX}/publicite`,
  agent: `${ADMIN_PREFIX}/agent`,
  users: `${ADMIN_PREFIX}/users`,
  userStats: `${ADMIN_PREFIX}/user-stats`,
  mobileUsers: `${ADMIN_PREFIX}/mobile-users`,
  accounts: `${ADMIN_PREFIX}/accounts`,
  invitationCodes: `${ADMIN_PREFIX}/invitation-codes`,
  notifications: `${ADMIN_PREFIX}/notifications`,
  campagnes: `${ADMIN_PREFIX}/campagnes`,
  content: `${ADMIN_PREFIX}/content`,
  featuredVideos: `${ADMIN_PREFIX}/featured-videos`,
  videoConfig: `${ADMIN_PREFIX}/video-config`,
  thumbnails: `${ADMIN_PREFIX}/thumbnails`,
  videosHls: `${ADMIN_PREFIX}/videos/hls`,
  videoProjects: `${ADMIN_PREFIX}/video-projects`,
  exerciseReports: `${ADMIN_PREFIX}/exercise-reports`,
  migrate: `${ADMIN_PREFIX}/migrate`,
  envSync: `${ADMIN_PREFIX}/env-sync`,
  logs: `${ADMIN_PREFIX}/logs`,
  crashReports: `${ADMIN_PREFIX}/crash-reports`,
  crashReportsConfig: `${ADMIN_PREFIX}/crash-reports-config`,
  moderationMessagerie: `${ADMIN_PREFIX}/moderation/messagerie`,
  iaSignalements: `${ADMIN_PREFIX}/ia-signalements`,
} as const;

export type AdminDomain = keyof typeof ADMIN_PATHS;

/**
 * Routes UTILISATEUR servies par la fonction `api` (fichiers
 * `functions/src/api/*`), sous `/admin/…` ou `/api/v1/…`,
 * avec la garde de rôle
 * de la route — PAS la garde admin du blueprint `/api/v1/admin`. Elles
 * restent ici volontairement : un compte back-office les appelle pour son
 * propre compte (profil). Ce ne sont pas des oublis du Lot 7.
 */
export const USER_PATHS = {
  /** Profil du compte connecté. */
  me: '/admin/users/me',
  /** Configuration marketplace en lecture (aussi lue par l'application). */
  marketplace: '/api/v1/marketplace',
  /** Métadonnées YouTube pour le pré-remplissage du formulaire vidéo. */
  youtubeMetadata: '/api/v1/videos/youtube-metadata',
} as const;

/** URL complète de chaque domaine admin (ADMIN_API_URL + chemin canonique). */
export const ADMIN_URLS = (Object.keys(ADMIN_PATHS) as AdminDomain[]).reduce(
  (urls, domain) => ({ ...urls, [domain]: `${ADMIN_API_URL}${ADMIN_PATHS[domain]}` }),
  {} as Record<AdminDomain, string>,
);
